import { useState } from 'react';
import BottomSheet from '../common/BottomSheet';
import ConfirmDialog from '../common/ConfirmDialog';
import { useApp } from '../../context/AppContext';
import { formatFullDateJP, parseDateKey } from '../../utils/dateUtils';

/**
 * "予定詳細画面" — opened by tapping an event card inside
 * SelectedDayBottomSheet (see openEventDetail). Shows every field the
 * event holds, plus 編集/削除 at the bottom.
 *
 *  編集 -> closes this sheet and opens EventFormSheet in mode="edit"
 *          (same form component as "add", per spec).
 *  削除 -> confirm dialog, then deletes from Google Calendar via
 *          removeEvent() (which re-runs GET internally), closes this
 *          sheet, and returns to the home screen — no explicit
 *          "navigate home" needed since this sheet/the day bottom
 *          sheet are the only things covering it.
 */
export default function EventDetailSheet() {
  const { eventDetail, closeEventDetail, openEditEventForm, removeEvent, showToast } = useApp();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const open = eventDetail != null;
  const ev = eventDetail;

  const handleEdit = () => {
    if (!ev) return;
    openEditEventForm(ev);
    closeEventDetail();
  };

  const handleDeleteConfirmed = async () => {
    if (!ev || deleting) return;
    setDeleting(true);
    try {
      await removeEvent(ev.id);
      // ⑦ホームへ戻る：このシートを閉じるだけでよい（既に選択日シートは
      // カードを開いた時点で閉じているため、下にはホーム画面しかない）。
      setConfirmOpen(false);
      closeEventDetail();
      showToast('予定を削除しました');
    } catch (err) {
      showToast(err?.message || '削除に失敗しました。もう一度お試しください。');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <BottomSheet
        open={open}
        onClose={closeEventDetail}
        title="予定の詳細"
        footer={
          <>
            <button className="btn btn-danger" type="button" onClick={() => setConfirmOpen(true)}>
              削除
            </button>
            <button className="btn btn-filled" type="button" onClick={handleEdit}>
              編集
            </button>
          </>
        }
      >
        {ev && (
          <>
            <div className="detail-hero">
              <div className="dh-icon" style={{ '--hero-color': ev.color || '#3F6659' }}>
                {ev.icon}
              </div>
              <div className="dh-title">{ev.title}</div>
            </div>

            <div className="detail-row">
              <div className="dr-icon">📅</div>
              <div>
                <div className="dr-label">日付</div>
                <div className="dr-value">{formatFullDateJP(parseDateKey(ev.date))}</div>
              </div>
            </div>

            <div className="detail-row">
              <div className="dr-icon">🕐</div>
              <div>
                <div className="dr-label">時間</div>
                <div className="dr-value">
                  {ev.allDay ? <span className="allday-chip">終日</span> : `${ev.start}〜${ev.end}`}
                </div>
              </div>
            </div>

            {ev.location && (
              <div className="detail-row">
                <div className="dr-icon">📍</div>
                <div>
                  <div className="dr-label">場所</div>
                  <div className="dr-value">{ev.location}</div>
                </div>
              </div>
            )}

            <div className="detail-row">
              <div className="dr-icon">🎨</div>
              <div>
                <div className="dr-label">色</div>
                <div className="dr-value dr-value--color">
                  <span className="dr-color-dot" style={{ background: ev.color || '#3F6659' }} />
                  アイコンの色
                </div>
              </div>
            </div>
          </>
        )}
      </BottomSheet>

      <ConfirmDialog
        open={confirmOpen}
        message="予定を削除しますか？"
        confirmLabel={deleting ? '削除中…' : '削除'}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => !deleting && setConfirmOpen(false)}
      />
    </>
  );
}
